import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ContentBox,
  ContentPage,
  DropdownAction,
  FullTable,
  ModalDefault,
  NoResults,
  Notice,
  PageActions,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import { getInvoiceDeadlineLabel, getPaymentRuleLabel } from "../../../../helpers";
import { closingDayLabel } from "@helpers/competencePeriod";
import { escapeHtml, usePageHeader } from "@components/ow";

// Página da API por requisição (o padrão do back)
const PAGE_SIZE = 50;

const LIST_HREF = "/c/contract-patterns";
const NEW_HREF = "/c/contract-patterns/new";
const BREADCRUMB = [{ page: "Padrões de contrato", route: LIST_HREF }];

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const editHref = (pattern) => `/c/contract-patterns/${pattern.id}/edit`;

export default function ContractPatterns() {
  const navigate = useNavigate();
  const patternService = useService("contractPatterns");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState({
    totalRegisters: 0,
    list: [],
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [selectedPattern, setSelectedPattern] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  usePageHeader({ title: "Padrões de contrato", breadcrumb: BREADCRUMB });

  useEffect(() => {
    fetchPatterns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize]);

  async function fetchPatterns() {
    setLoading(true);
    try {
      const result = await patternService.list(`page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}`);
      if (result && result.data) {
        setPaginationData(result.data);
      } else {
        throw new Error("Erro ao carregar padrões");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao carregar padrões");
      setPaginationData({ totalRegisters: 0, list: [], totalPages: 1 });
    }
    setLoading(false);
  }

  function closeDeleteModal() {
    if (loadingDelete) return;
    setSelectedPattern(null);
  }

  async function handleDelete() {
    setLoadingDelete(true);
    try {
      const result = await patternService.delete(selectedPattern.id);
      if (result && result.data && result.data.error) {
        throw new Error(result.data.msg);
      }
      toast.success("Padrão excluído com sucesso!");
      setSelectedPattern(null);
      fetchPatterns();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao excluir padrão");
    }
    setLoadingDelete(false);
  }

  async function handleDuplicate(pattern) {
    try {
      const result = await patternService.duplicate(pattern.id);
      if (result && !result.error) {
        toast.success("Padrão duplicado com sucesso!");
        fetchPatterns();
      } else {
        throw new Error(result?.msg || "Erro ao duplicar padrão");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao duplicar padrão");
    }
  }

  const columns = [
    {
      id: "name",
      header: "Nome",
      size: 260,
      maxSize: 340,
      cell: ({ row }) => <Text $text={escapeHtml(row.original.name)} $variant="paragraph_small_bold" $color="N900" />,
    },
    {
      id: "invoice",
      header: "Prazo da nota fiscal",
      size: 200,
      cell: ({ row }) =>
        getInvoiceDeadlineLabel(row.original.invoice_deadline_type, row.original.invoice_deadline, row.original.invoice_deadline_month),
    },
    {
      id: "payment",
      header: "Regra de pagamento",
      size: 200,
      cell: ({ row }) =>
        getPaymentRuleLabel(row.original.payment_rule_type, row.original.payment_rule_value, row.original.payment_rule_month),
    },
    {
      id: "closing",
      header: "Apuração de horas",
      size: 180,
      cell: ({ row }) => capitalize(closingDayLabel(row.original.closing_day)),
    },
    { id: "contracts", header: "Contratos", size: 100, align: "right", cell: ({ row }) => row.original.contractCount || 0 },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction
          options={[
            { label: "Editar", action: () => navigate(editHref(row.original)) },
            { label: "Duplicar", action: () => handleDuplicate(row.original) },
            { label: "Excluir", action: () => setSelectedPattern(row.original), isImportant: true },
          ]}
        />
      ),
    },
  ];

  const inUse = selectedPattern?.contractCount > 0;

  return (
    <ContentPage>
      <PageActions>
        <Button $text="Novo padrão" $variant="primary" $action={() => navigate(NEW_HREF)} />
      </PageActions>

      <ContentBox>
        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          onRowClick={(pattern) => navigate(editHref(pattern))}
          noResults={
            <NoResults
              notResultText="Nenhum padrão cadastrado"
              includeText="Novo padrão"
              includeAction={() => navigate(NEW_HREF)}
            />
          }
        />
      </ContentBox>

      {selectedPattern && (
        <ModalDefault
          $showModal={!!selectedPattern}
          $setShowModal={closeDeleteModal}
          $noClose={loadingDelete}
          $size="medium"
          $title="Excluir padrão"
          $contentBackground="N0"
          $footer={
            <>
              <Button $text="Cancelar" $variant="outline" $action={closeDeleteModal} $isDisabled={loadingDelete} $noWidth />
              <Button
                $text="Excluir padrão"
                $variant="danger"
                $action={handleDelete}
                $isLoading={loadingDelete}
                $isDisabled={inUse}
                $noWidth
              />
            </>
          }
        >
          <ModalContent pattern={selectedPattern} inUse={inUse} />
        </ModalDefault>
      )}
    </ContentPage>
  );
}

function ModalContent({ pattern, inUse }) {
  return (
    <ModalBody>
      <Text
        $text="Deseja excluir o padrão <strong>%s</strong>?"
        $replaces={[escapeHtml(pattern.name)]}
        $variant="paragraph_small_medium"
        $color="N700"
        $colorEmphasis="N900"
      />
      {inUse ? (
        <Notice
          $variant="orange"
          $title={`Usado em ${plural(pattern.contractCount, "contrato", "contratos")}`}
          $text="Não é possível excluir enquanto houver contratos com este padrão. Troque o padrão desses contratos antes."
        />
      ) : (
        <Text
          $text="O padrão sai da lista e deixa de aparecer no cadastro de contratos."
          $variant="paragraph_small_medium"
          $color="N700"
        />
      )}
    </ModalBody>
  );
}
